import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authAPI from '../../utils/api/auth';
import config from '../../config';
import { clearUserState } from './userSlice';
import { clearMosadsState } from './mosadsSlice';
import { clearCustomersState } from './customersSlice';
import { clearTransactionsState } from './transactionsSlice';
import { clearBanksState } from './banksSlice';
import { clearEmployeesState } from './employeesSlice';

function authErrorToMessage(payload) {
  if (payload == null) return 'שגיאה';
  if (typeof payload === 'string') return payload;
  if (typeof payload.message === 'string') return payload.message;
  if (Array.isArray(payload.message)) return payload.message.join(', ');
  if (typeof payload.error === 'string') return payload.error;
  return 'שגיאה';
}

// Async actions
export const login = createAsyncThunk(
  'api/auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      localStorage.setItem(config.APP_NAME + '_token', response.token);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error.message ?? 'שגיאת התחברות');
    }
  }
);

export const register = createAsyncThunk(
  'api/auth/register',
  async ({ empId, mosadSn, ...userData }, { rejectWithValue }) => {
    try {
      const response = await authAPI.register({ empId, mosadSn, ...userData });
      localStorage.setItem(config.APP_NAME + '_token', response.token);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error.message ?? 'שגיאת הרשמה');
    }
  }
);

export const googleLogin = createAsyncThunk(
  'api/auth/googleLogin',
  async (token, { rejectWithValue }) => {
    try {
      const response = await authAPI.googleLogin(token);
      localStorage.setItem(config.TOKEN_KEY, response.data.token);
      localStorage.setItem(config.USER_KEY, JSON.stringify(response.data.user));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'שגיאת התחברות עם Google');
    }
  }
);

export const changePassword = createAsyncThunk(
  'api/auth/changePassword',
  async (passwordData, { rejectWithValue }) => {
    try {
      const response = await authAPI.changePassword(passwordData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'שגיאת שינוי סיסמה');
    }
  }
); 

export const resetPassword = createAsyncThunk(
  'api/auth/resetPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await authAPI.resetPassword(email);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'שגיאת איפוס סיסמה');
    }
  }
);  

export const logout = createAsyncThunk(
  '/logout',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      localStorage.removeItem(config.APP_NAME + '_token');
      localStorage.removeItem(config.APP_NAME + '_selectedMosadCode');
      localStorage.removeItem(config.APP_NAME + '_firstLoginCompleted');
      localStorage.removeItem(config.APP_NAME + '_mosadToken');
      dispatch(clearAuthState());
      dispatch(clearUserState());
      dispatch(clearMosadsState());
      dispatch(clearCustomersState());
      dispatch(clearTransactionsState());
      dispatch(clearBanksState());
      dispatch(clearEmployeesState());
      return null;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error.message ?? 'שגיאת התנתקות');
    }
  }
);

// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem(config.APP_NAME + '_token'),
  isAuthenticated: null,
  loading: false,
  error: null
};

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuthState: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = authErrorToMessage(action.payload);
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        // state.token = action.payload.token;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = authErrorToMessage(action.payload);
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      // Google Login
      .addCase(googleLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = authErrorToMessage(action.payload);
      })
      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = authErrorToMessage(action.payload);
      });
  }
});

export const { clearError, clearAuthState } = authSlice.actions;

export default authSlice.reducer; 